/**
 * routeHandoff.js — one-shot state handed from the page you clicked FROM to the page
 * you land ON.
 *
 * WHY NOT A QUERY PARAM
 *   The obvious shape is `#/leads?campaign=spring`. It doesn't survive: mvvLegit owns
 *   the fragment and strips unknown tails during its route guard, so params vanish
 *   before the destination route ever reads them (see the mvvlegit-auth notes — the
 *   same trap behind "params disappear from the URL" and email-link landings).
 *
 * WHY ONE-SHOT
 *   Claiming clears the value. Without that, a drill-through would silently re-apply
 *   itself every later visit to the destination — you'd search for a campaign once and
 *   find the leads page mysteriously filtered days later.
 *
 * TRADEOFF, STATED PLAINLY
 *   This is in-memory module state, so it does not survive a reload or a new tab. A
 *   middle-clicked drill-through lands on the unfiltered destination. That is a
 *   deliberate, graceful degradation rather than a bug: the alternative is persisting
 *   UI intent to storage, which then needs its own expiry and staleness rules.
 *
 * Second use of this pattern (magnet templates → /magnets was the first), extracted at
 * instance two for the same reason stepTypes.js and magnetAppearance.js exist.
 */

import AuthController from '../services/AuthController.js';

// Keys are declared here rather than passed as loose strings, so a typo is a missing
// import instead of a hand-off that silently never arrives.
export const HANDOFF = Object.freeze({
  /** Template id chosen on /templates, claimed by /magnets to start that flow. */
  MAGNET_TEMPLATE: 'magnet_template',
  SHOPIFY_CREATOR: 'shopify_creator',
  /** Search text set from a dashboard drill-through, claimed by /leads. */
  LEADS_SEARCH: 'leads_search',
  /**
   * A single YYYY-MM-DD day, set by clicking a column on the dashboard's trend
   * chart, claimed by /leads. UTC, matching how the chart's own buckets are keyed —
   * see the day filter in leads-route.
   */
  LEADS_DAY: 'leads_day',
  LEADS_CONTEXT: 'leads_context',
  MAGNET_RESULTS_CONTEXT: 'magnet_results_context',
});

const pending = new Map();

export function setHandoff(key, value) {
  pending.set(key, value);
}

/** Read and clear. Returns null when nothing is waiting. */
export function takeHandoff(key) {
  if (!pending.has(key)) return null;
  const value = pending.get(key);
  pending.delete(key);
  return value;
}

/**
 * Read WITHOUT clearing. For callers that need to know what is parked but are not
 * the claimant — a page rendering "you picked X" before the destination route runs,
 * or a staleness check that must not burn the one delivery takeHandoff() grants.
 * Returns null when nothing is waiting.
 */
export function peekHandoff(key) {
  if (!pending.has(key)) return null;
  return pending.get(key);
}

/** Drop a parked value without consuming it (e.g. the user navigated elsewhere). */
export function clearHandoff(key) {
  pending.delete(key);
}

// Explicit drill-through context. An owner filter never changes the active client;
// the destination must still validate it against its authorized server scope.
// One-shot memory deliberately avoids leaving a filter behind on ordinary visits.
const CONTEXT_TTL = 15 * 60 * 1000;
const identifier = value => typeof value === 'string' && /^[\w-]{1,128}$/.test(value);
const day = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
export const leadsHandoffScope = () => ({
  actorGuid: AuthController.getEntity?.()?.guid || null,
  orgGuid: AuthController.getStoredActiveOrg?.() || AuthController.getScope?.()?.activeOrg?.guid || AuthController.getActiveOrg?.()?.guid || null,
});

function normalizeContext(input) {
  if (!input || !identifier(input.actorGuid) || (input.orgGuid != null && !identifier(input.orgGuid))) return null;
  const context = { actorGuid: input.actorGuid, orgGuid: input.orgGuid || null };
  for (const key of ['ownerGuid', 'magnetGuid', 'leadGuid', 'leadId']) {
    if (input[key] == null || input[key] === '') continue;
    const value = String(input[key]);
    if (!identifier(value)) return null;
    context[key] = value;
  }
  const from = input.day ?? input.from, to = input.day ?? input.to;
  if (from != null || to != null) {
    if (!day(from) || !day(to) || from > to) return null;
    context.from = from; context.to = to;
  }
  if (input.search != null) {
    if (typeof input.search !== 'string' || input.search.length > 200) return null;
    context.search = input.search;
  }
  // Capture-source and status filters, so a report can land on "Google Ads call
  // leads still NEW in this range" rather than on a search term that approximates it.
  // Plain strings the leads page already accepts as filter values; the server
  // validates them again.
  for (const key of ['source', 'status']) {
    if (input[key] == null || input[key] === '') continue;
    if (typeof input[key] !== 'string' || input[key].length > 50 || /[\n\r\t<>]/.test(input[key])) return null;
    context[key] = input[key];
  }
  if (input.returnTo != null) {
    const { hash, label } = input.returnTo;
    if (typeof hash !== 'string' || !/^#\/[\w%/-]*$/.test(hash) || hash.includes('..')
      || typeof label !== 'string' || !label.trim() || label.length > 100) return null;
    context.returnTo = { hash, label };
  }
  return context;
}

function setContext(key, input) {
  clearHandoff(key);
  const context = normalizeContext(input);
  if (!context) return false;
  setHandoff(key, { ...context, expiresAt: Date.now() + CONTEXT_TTL });
  return true;
}
function takeContext(key, scope) {
  const context = takeHandoff(key);
  if (!context || !scope || context.expiresAt < Date.now() || context.actorGuid !== scope.actorGuid
    || context.orgGuid !== (scope.orgGuid || null)) return null;
  const { expiresAt, ...value } = context;
  return value;
}
export const setLeadsHandoff = input => setContext(HANDOFF.LEADS_CONTEXT, input);
export function takeLeadsHandoff(scope = leadsHandoffScope()) {
  const existed = peekHandoff(HANDOFF.LEADS_CONTEXT) != null;
  const context = takeContext(HANDOFF.LEADS_CONTEXT, scope);
  // Legacy values parked alongside a typed handoff must never survive its claim,
  // including a rejected claim after an account/client change.
  if (existed) { clearHandoff(HANDOFF.LEADS_SEARCH); clearHandoff(HANDOFF.LEADS_DAY); }
  return context;
}
export const setMagnetResultsHandoff = input => setContext(HANDOFF.MAGNET_RESULTS_CONTEXT, input);
export function takeMagnetResultsHandoff(guid, scope = leadsHandoffScope()) {
  const context = takeContext(HANDOFF.MAGNET_RESULTS_CONTEXT, scope);
  return context?.magnetGuid === guid ? context : null;
}

export default { HANDOFF, setHandoff, takeHandoff, peekHandoff, clearHandoff };
